import { useCallback, useEffect, useState } from "react";
import { Button, Card, Input, Stack } from "@salesapp/ui";
import { opsApi, type ConfigurableFieldOptions } from "../../lib/opsApi";
import { useToast } from "@salesapp/ui";
import { optionsCsv } from "./SearchBar";
import { reportSaveBlocked, reportSaveFailure } from "../../lib/saveNotice";

/**
 * Per-tab settings drawer body: only this tab's own registry-backed dropdown
 * fields (e.g. Products -> product_category, unit_of_measure), not every
 * entity's options like the central Field Options page shows. Same
 * crm.field_options capability/endpoints, filtered client-side by entity.
 */
export function TabFieldOptionsSettings({ orgId, entity }: { orgId: string; entity: string }) {
  const { show } = useToast();
  const [fields, setFields] = useState<ConfigurableFieldOptions[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await opsApi.listFieldOptions(orgId);
      const scoped = result.fields.filter((field) => field.entity === entity);
      setFields(scoped);
      setDrafts(Object.fromEntries(scoped.map((field) => [field.field_key, optionsCsv(field.options)])));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Field options could not be loaded.");
    }
  }, [orgId, entity]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(field: ConfigurableFieldOptions, override?: string[]) {
    const options = override ?? (drafts[field.field_key] ?? "").split(",").map((item) => item.trim()).filter(Boolean);
    if (options.length === 0) {
      setError(reportSaveBlocked(show, `${field.label} needs at least one option.`));
      return;
    }
    setSavingKey(field.field_key);
    setError(null);
    try {
      const updated = await opsApi.saveFieldOptions(orgId, field.entity, field.field_key, options);
      setFields((current) => (current ?? []).map((item) => (item.field_key === field.field_key ? updated : item)));
      setDrafts((current) => ({ ...current, [field.field_key]: optionsCsv(updated.options) }));
      show(override ? `${field.label} options restored to defaults.` : `${field.label} options saved.`, "success");
    } catch (err) {
      setError(reportSaveFailure(show, err, "Options could not be saved."));
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <Stack gap={4}>
      {error && <p className="crm-error">{error}</p>}
      {!fields ? (
        <p className="crm-muted">Loading dropdown options…</p>
      ) : fields.length === 0 ? (
        <p className="crm-muted">There are no configurable dropdowns on this tab yet.</p>
      ) : (
        fields.map((field) => {
          const dirty = drafts[field.field_key] !== optionsCsv(field.options);
          const defaults = field.default_options ?? [];
          const atDefaults = optionsCsv(field.options) === optionsCsv(defaults);
          const busy = savingKey === field.field_key;
          return (
            <Card key={field.field_key}>
              <Stack gap={2}>
                <h2 className="crm-section-title">{field.label}</h2>
                <p className="crm-muted">
                  Values used across boards, filters, and record forms wherever {field.label.toLowerCase()} appears.
                </p>
                <Input
                  label="Options (comma-separated)"
                  value={drafts[field.field_key] ?? ""}
                  onChange={(event) =>
                    setDrafts((current) => ({ ...current, [field.field_key]: event.target.value }))
                  }
                />
                {defaults.length > 0 && (
                  <p className="crm-muted">
                    Defaults: <strong>{optionsCsv(defaults)}</strong>
                    {atDefaults && " (currently in use)"}
                  </p>
                )}
                <Button onClick={() => save(field)} disabled={!dirty || busy}>
                  {busy ? "Saving…" : "Save"}
                </Button>
                {defaults.length > 0 && (
                  <Button variant="secondary" onClick={() => save(field, defaults)} disabled={atDefaults || busy}>
                    Restore defaults
                  </Button>
                )}
              </Stack>
            </Card>
          );
        })
      )}
    </Stack>
  );
}
